/**
 * BRAND-TOKENS-1 — the ONE place the brand's values live.
 *
 * Ruled at s191 (option d, D-191.17): put the brand behind a single module *and then* repaint.
 * This is the first half. 🔴 It is deliberately NOT a repaint — every value below is the value that
 * was already in the code, moved, not changed. The new palette does not exist yet; Reade has said he
 * wants to change it and has not chosen it, so repainting now would be repainting to the colours he
 * is replacing.
 *
 * WHY IT LIVES IN `shared/`: the brand crosses the client/server line. `server/email.ts` holds 8 of
 * the sage occurrences and the product name; the marketing pages hold the rest. A client-only module
 * would have forced a second copy on the server, which is the problem this module exists to end.
 *
 * WHAT BELONGS HERE: colour, product name, typeface, domain — the things that change when the brand
 * changes. WHAT DOES NOT: layout, spacing, component styling. If a value would survive a rebrand
 * unchanged, it is not a brand token and does not belong in this file.
 *
 * 🔴 The Tailwind palette in `tailwind.config.ts` is the OTHER half of the same truth and is not yet
 * merged into this one. `tailwind.config.ts` is consumed by the Tailwind build, which cannot import a
 * TS module at config time without a build change that is out of this pilot's scope. Until that is
 * done, a colour change means editing BOTH. That duplication is deliberate, named here so the next
 * reader does not assume this file is already the single source, and it is priced in the pilot's
 * report as remaining work.
 */

/** The sage ramp — the primary brand colour. Values lifted verbatim from `tailwind.config.ts`. */
export const SAGE = {
  50: "#F1F5ED",
  100: "#DDE5D4",
  200: "#B8C8A8",
  400: "#7A9278",
  600: "#4F6B51",
  800: "#2E4030",
  900: "#1C2820",
} as const;

/** The clay ramp — the secondary accent. */
export const CLAY = {
  100: "#F5E6DB",
  400: "#C7906D",
  600: "#A56E4B",
  800: "#6F4526",
} as const;

/** The warm neutrals — page grounds, rules and text. */
export const WARM = {
  white: "#FEFCF8",
  cream: "#FAF7F1",
  stone: "#D9D1C1",
  stoneLight: "#ECE7DC",
  taupe: "#9B9383",
  /** s221 Lane 1b: the interview board's muted text (Main.dc.html, its most-used colour).
   *  s233 (D-233.37): darkened everywhere, #6B655A → #4A4640. */
  muted: "#4A4640",
  charcoal: "#2B2A26",
  ink: "#1A1915",
} as const;

/**
 * The typefaces, as `font-family` stacks ready to assign.
 *
 * 🟢 s208 (UI-FLOOR-1) — THE FALLBACK IS FIXED, and this is a DELIBERATE change to computed output.
 *
 * The s205 pilot found the defect and left it alone on purpose: the serif stack was `Fraunces,serif`
 * — one webfont and the generic default — so a failed Fraunces load rendered the wordmark in Times
 * New Roman on most Windows machines. The pilot's contract was byte-identical output, and widening
 * a stack changes the computed `font-family` on every headline, so it was held for a phase that is
 * allowed to change what renders. This is that phase.
 *
 * 🔴 The chains below are ordered by METRIC SIMILARITY, not by preference: each fallback is a face
 * whose proportions are close enough that a failed webfont load shifts the page a little rather than
 * reflowing it. Georgia and Iowan Old Style stand in for Fraunces' high-contrast old-style serif;
 * Segoe UI and Roboto stand in for DM Sans' geometric sans. The generic keyword stays LAST so a
 * machine with none of them still gets the right CATEGORY.
 *
 * 🔴 The codebase used FOUR spellings of these two stacks before this change — `"Fraunces, serif"`
 * (22), `"Fraunces,serif"` (4), `"DM Sans, sans-serif"` (16) and `"Georgia,serif"` (2, the
 * email-safe substitute). They are now one each. That is why the font repoint is HERE and not in the
 * token pass: substituting a token for a string that does not match it is a restyle wearing a
 * repoint's clothes.
 */
export const FONT = {
  /** The display/serif face — headlines and the wordmark. */
  serif: "Fraunces, 'Iowan Old Style', Georgia, 'Times New Roman', serif",
  /** The text/sans face — body copy and UI. */
  sans: "'DM Sans', system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  /**
   * The serif stack for EMAIL bodies.
   *
   * 🔴 Deliberately NOT `serif` above. Mail clients do not load webfonts reliably and several strip
   * unknown family names outright, so an email asks for faces that are already on the machine. It is
   * still a brand value — it is the stand-in for Fraunces — which is why it lives here rather than
   * being typed into `server/email.ts`.
   */
  emailSerif: "Georgia, 'Times New Roman', serif",
} as const;

/** The product name, wherever it is shown to a person or written into an email. */
export const PRODUCT_NAME = "Organized Estate";

/**
 * The public domain and the addresses derived from it.
 *
 * 🔴 There are TWO live domains in the codebase today, measured s205: `organized-estate.com` (the
 * marketing/app host, 12 occurrences) and `organizedestate.ca` (the support address in
 * `server/email.ts`). They are NOT the same string and neither is a typo for the other. Both are
 * recorded here rather than silently unified, because choosing between them is Reade's call and
 * part of the name change, not a token move.
 */
export const DOMAIN = {
  primary: "organized-estate.com",
  supportEmailDomain: "organizedestate.ca",
} as const;

/**
 * The EMAIL neutrals — every non-brand colour the email templates use (s229, D-229.100).
 *
 * 🔴 Recorded VERBATIM, not unified: these are the greys the templates already carried, moved here so
 * email HTML holds no raw colour (the brand guard now fails on one — brand-tokens-parity.test.ts).
 * Swapping them for the warm neutrals above is a restyle of every email and is Reade's call, the same
 * way DOMAIN records two live domains without choosing. The legacy teal (#1ABC9C) is NOT here: it
 * was never the brand, and its uses became SAGE_600.
 */
export const EMAIL = {
  /** Body text. */
  text: "#1A202C",
  /** Secondary body text. */
  body: "#4A5568",
  /** Sign-offs and small print. */
  muted: "#718096",
  /** The finest print (links spelled out, footers). */
  faint: "#A0AEC0",
  /** A boxed detail panel and its rule. */
  panel: "#F8F9FA",
  panelRule: "#E2E8F0",
  /** The sage-tinted panel (the advisor welcome's steps) and its rule. */
  sagePanel: "#F4F7F4",
  sagePanelRule: "#C8D8C9",
  /** The password-reset frame: canvas, card, header band, footer band and its rule. */
  canvas: "#F5F5EE",
  card: "#FFFFFF",
  /** Text on a dark band or a filled button. */
  onDark: "#FFFFFF",
  header: "#2D4A3E",
  footer: "#F9F9F6",
  footerRule: "#E8E8E0",
} as const;

/** Convenience aliases for the two values the codebase reaches for most. */
export const SAGE_600 = SAGE[600];
export const WARM_WHITE = WARM.white;
